/**
 * View-independent logic for the shared Router component.
 *
 * Everything in here is pure (no React, no DOM) so it can be unit-tested
 * with vitest in a node environment.
 */

export type RouterView = "matrix" | "select" | "buttons";

export const ROUTER_VIEWS: readonly RouterView[] = [
  "matrix",
  "select",
  "buttons",
] as const;

export interface RouterPort {
  id: string;
  /** Current name */
  label: string;
  /** Factory name; when it differs from `label` it is shown muted and can be restored */
  defaultLabel?: string;
  /** Grouping (e.g. Dante device name, "1–10") */
  group?: string;
  /** e.g. offline device: cannot be routed/renamed */
  disabled?: boolean;
}

export type RouteState = "ok" | "pending" | "error" | "unresolved";

export type Routes = Record<string, string | null>;

/* ─────────────────────────────────────────────────────────── */
/* Grouping                                                     */
/* ─────────────────────────────────────────────────────────── */

export interface PortGroup {
  /** Stable key; "" for ports without a group */
  key: string;
  label: string;
  ports: RouterPort[];
}

/**
 * Group ports by their `group`, keeping groups in order of first appearance
 * and ports in their original order within each group.
 */
export function groupPorts(ports: RouterPort[]): PortGroup[] {
  const byKey = new Map<string, PortGroup>();
  const groups: PortGroup[] = [];
  for (const port of ports) {
    const key = port.group ?? "";
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: key, ports: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.ports.push(port);
  }
  return groups;
}

/** Whether group headers are worth rendering (any named group present). */
export function hasNamedGroups(groups: PortGroup[]): boolean {
  return groups.some((g) => g.key !== "");
}

/**
 * Split ungrouped ports into pages of `size` (labelled "1–10", "11–20", …)
 * so large flat lists stay manageable in the buttons view. Ports that
 * already carry a group are grouped normally.
 */
export function groupPortsPaged(
  ports: RouterPort[],
  size: number,
): PortGroup[] {
  const groups = groupPorts(ports);
  if (hasNamedGroups(groups) || ports.length <= size) return groups;
  const pages: PortGroup[] = [];
  for (let i = 0; i < ports.length; i += size) {
    const label = pageGroupLabel(i, ports.length, size);
    pages.push({ key: label, label, ports: ports.slice(i, i + size) });
  }
  return pages;
}

/** Label for the page containing the item at `index` (0-based), e.g. "11–20". */
export function pageGroupLabel(
  index: number,
  total: number,
  size: number,
): string {
  const start = Math.floor(index / size) * size;
  return `${start + 1}–${Math.min(start + size, total)}`;
}

/** Toggle a group key in a set of collapsed groups (returns a new set). */
export function toggleGroup(
  collapsed: ReadonlySet<string>,
  key: string,
): Set<string> {
  const next = new Set(collapsed);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/* ─────────────────────────────────────────────────────────── */
/* Route lookup                                                 */
/* ─────────────────────────────────────────────────────────── */

export function indexPorts(ports: RouterPort[]): Map<string, RouterPort> {
  return new Map(ports.map((p) => [p.id, p]));
}

/** Source id routed to a target, or null. */
export function routedSourceId(
  routes: Routes,
  targetId: string,
): string | null {
  return routes[targetId] ?? null;
}

export interface ResolvedRoute {
  sourceId: string | null;
  /** The source port, when its id is present in `sources` */
  source: RouterPort | undefined;
  /** Text to show for the route ("" when nothing is routed) */
  label: string;
  state: RouteState | undefined;
}

/**
 * Resolve what a target is currently receiving, including a fallback label
 * for routes whose source isn't in the known sources (e.g. an offline Dante
 * device), and the effective state of the route.
 */
export function resolveRoute(
  targetId: string,
  routes: Routes,
  sourcesById: Map<string, RouterPort>,
  routeState?: Record<string, RouteState>,
  routeFallbackLabel?: Record<string, string>,
): ResolvedRoute {
  const sourceId = routedSourceId(routes, targetId);
  if (sourceId === null) {
    const fallback = routeFallbackLabel?.[targetId];
    return {
      sourceId: null,
      source: undefined,
      label: fallback ?? "",
      state: fallback ? (routeState?.[targetId] ?? "unresolved") : undefined,
    };
  }
  const source = sourcesById.get(sourceId);
  const explicit = routeState?.[targetId];
  return {
    sourceId,
    source,
    label: source?.label ?? routeFallbackLabel?.[targetId] ?? sourceId,
    state: explicit ?? (source ? "ok" : "unresolved"),
  };
}

/** Number of targets in `targetIds` routed to any source in `sourceIds`. */
export function countRoutesBetween(
  routes: Routes,
  targetIds: Iterable<string>,
  sourceIds: ReadonlySet<string>,
): number {
  let count = 0;
  for (const t of targetIds) {
    const s = routes[t];
    if (s != null && sourceIds.has(s)) count++;
  }
  return count;
}

/* ─────────────────────────────────────────────────────────── */
/* Route changes                                                */
/* ─────────────────────────────────────────────────────────── */

/**
 * Decide what clicking a source for a target should do.
 * Returns the new source id (null = clear), or `undefined` for a no-op.
 */
export function nextRoute(
  currentSourceId: string | null,
  clickedSourceId: string,
  allowClear: boolean,
): string | null | undefined {
  if (currentSourceId === clickedSourceId) {
    return allowClear ? null : undefined;
  }
  return clickedSourceId;
}

export interface RoutableArgs {
  target: RouterPort | undefined;
  source?: RouterPort | undefined;
  disabled?: boolean;
  hasHandler: boolean;
}

export function canRoute({
  target,
  source,
  disabled,
  hasHandler,
}: RoutableArgs): boolean {
  if (!hasHandler || disabled || !target || target.disabled) return false;
  if (source?.disabled) return false;
  return true;
}

/* ─────────────────────────────────────────────────────────── */
/* Renaming                                                     */
/* ─────────────────────────────────────────────────────────── */

export function canRename(
  port: RouterPort,
  hasHandler: boolean,
  disabled?: boolean,
): boolean {
  return hasHandler && !disabled && !port.disabled;
}

/** Whether the port has been renamed away from its factory name. */
export function isRenamed(port: RouterPort): boolean {
  return port.defaultLabel !== undefined && port.defaultLabel !== port.label;
}

/**
 * Normalise the value typed into the rename input.
 * Returns the new name, null to reset to default, or `undefined` for no change.
 */
export function normaliseRename(
  port: RouterPort,
  value: string,
): string | null | undefined {
  const trimmed = value.trim();
  if (trimmed === "") {
    // Already showing the factory name: nothing to reset
    return port.defaultLabel === port.label ? undefined : null;
  }
  if (trimmed === port.label) return undefined;
  return trimmed;
}

/* ─────────────────────────────────────────────────────────── */
/* View persistence                                             */
/* ─────────────────────────────────────────────────────────── */

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function viewStorageKeyFor(viewStorageKey: string): string {
  return `router-view:${viewStorageKey}`;
}

export function isRouterView(value: unknown): value is RouterView {
  return (
    typeof value === "string" &&
    (ROUTER_VIEWS as readonly string[]).includes(value)
  );
}

export function readStoredView(
  storage: KeyValueStorage | undefined,
  viewStorageKey: string,
  fallback: RouterView,
): RouterView {
  try {
    const stored = storage?.getItem(viewStorageKeyFor(viewStorageKey));
    return isRouterView(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
}

export function writeStoredView(
  storage: KeyValueStorage | undefined,
  viewStorageKey: string,
  view: RouterView,
): void {
  try {
    storage?.setItem(viewStorageKeyFor(viewStorageKey), view);
  } catch {
    // storage unavailable (private mode, quota) — not fatal
  }
}
